export type LoadMode =
  "stack_kg" | "kg_each" | "kg_total" | "bodyweight" | "count";

export interface Profile {
  id: string;
  displayName: string;
  phoneMasked?: string;
  isAdmin: boolean;
  timezone: string;
}

export interface Exercise {
  id: string;
  slug: string;
  name: string;
  equipment: string;
  primaryMuscles: string[];
  loadMode: LoadMode;
  description: string;
  cues: string[];
  commonMistakes: string[];
  videoUrl: string;
  videoSourceName: string;
  defaultIncrement: number | null;
  loadOptions: number[];
  isUnilateral: boolean;
  specialTarget: number | null;
}

export interface TemplateExercise {
  /** Done for now; the original set prescription stays intact for statistics. */
  finishedEarly?: boolean;
  id: string;
  exercise: Exercise;
  supersetGroup: string;
  positionInPair: number;
  sortOrder: number;
  sets: number;
  repMin: number | null;
  repMax: number | null;
  restSeconds: number;
  targetCount: number | null;
  notes: string;
  lastPerformance?: {
    weightValue: number | null;
    reps: number[];
    countValue: number | null;
  } | null;
  progressionFlag?: {
    id: string;
    suggestedIncrement: number | null;
    suggestedTarget?: number | null;
  } | null;
}

export interface WorkoutTemplate {
  id: string;
  version?: number;
  code: string;
  name: string;
  subtitle: string;
  rotationOrder: number;
  exercises: TemplateExercise[];
}

export interface Pointer {
  groupIndex: number;
  round: number;
  position: number;
  restEndAt: string | null;
  /** True when no exercise is selected and every unfinished exercise is available. */
  selectionRequired?: boolean;
}

export interface ActiveSession {
  profileId?: string;
  updatedAt?: string;
  pausedAt?: string | null;
  closedAt?: string | null;
  completedAt?: string | null;
  exerciseEvents?: Array<{ kind: string; exerciseName: string; detail?: string | null }>;
  status?: string;
  scheduledFor?: string | null;
  id: string;
  template: WorkoutTemplate;
  startedAt: string;
  firstSetAt?: string | null;
  finalSetAt?: string | null;
  elapsedSeconds?: number | null;
  pointer: Pointer;
  sets: SetResult[];
  notes?: string;
}

export interface SetResult {
  name?: string;
  loadMode?: LoadMode;
  editedAt?: string | null;
  id?: string;
  clientEventId: string;
  templateExerciseId: string;
  exerciseId: string;
  setNumber: number;
  weightValue: number | null;
  reps: number | null;
  countValue: number | null;
  completedAt: string;
  previousPointer?: Pointer | null;
  pending?: boolean;
}

export interface NextWorkout {
  occurrenceVersion: number;
  nextTemplate: { id: string; code: string; name: string } | null;
  template: WorkoutTemplate;
  scheduledDate: string;
  isScheduledToday: boolean;
  lastSession: {
    code: string;
    name?: string;
    templateId: string | null;
    repeatUnavailableReason?: string | null;
    completedAt: string;
    elapsedSeconds: number;
  } | null;
  week: Array<{
    weekday: number;
    date: string;
    label: string;
    status: "done" | "skipped" | "next" | "upcoming" | "missed";
  }>;
}

export interface ProfileSettings {
  compoundRest: number;
  isolationRest: number;
  chimeEnabled: boolean;
  alertMode: "voice" | "soft_chime" | "off";
  keepAwake: boolean;
  morningReminder: boolean;
  morningReminderTime: string;
  reminderTime: string;
  reminderEnabled: boolean;
  timezone: string;
  scheduleDays: number[];
}

export const defaultProfileSettings: ProfileSettings = {
  compoundRest: 90,
  isolationRest: 60,
  chimeEnabled: true,
  alertMode: "voice",
  keepAwake: true,
  morningReminder: false,
  morningReminderTime: "07:00",
  reminderTime: "19:00",
  reminderEnabled: true,
  timezone: "Australia/Brisbane",
  scheduleDays: [1, 3, 5],
};
